import axiosInstance from '@/setup/axios/axiosInstance';
import { DEFAULT_PAGE_SIZE } from '@/__core/constants/pagination';
import { getCurrentCompanyId } from '@/modules/auth/helpers/CompanyStorage';

import { InventoryIssueLineRow, InventoryIssueListResponse, InventoryIssueQueryParams, InventoryIssueRow } from '../types';

const PAGE_SIZE = DEFAULT_PAGE_SIZE;

interface ApiIssueLine {
  id?: string;
  item_id?: string;
  item_code?: string;
  item_name?: string;
  order_no?: string;
  order_code?: string;
  source_order_no?: string;
  source_order_code?: string;
  order?: ApiRelation | null;
  uom_id?: string;
  uom_name?: string;
  issued_qty?: string | number | null;
  unit_cost?: string | number | null;
  in_progress_qty?: string | number | null;
  line_amount?: string | number | null;
  note?: string;
  [key: string]: any;
}

interface ApiRelation {
  id?: string;
  code?: string;
  name?: string;
  username?: string;
  [key: string]: any;
}

interface ApiMaterialRequestRelation {
  id?: string;
  request_no?: string;
}

interface ApiStatusRelation {
  id?: string;
  code?: string;
  name?: string;
}

interface ApiIssue {
  id: string;
  issue_no?: string;
  content?: string;
  issued_at?: string | null;
  status?: string | ApiStatusRelation | null;
  status_id?: string;
  status_code?: string;
  status_display?: string;
  company?: ApiRelation | null;
  company_id?: string;
  company_name?: string;
  warehouse?: ApiRelation | null;
  warehouse_id?: string;
  warehouse_name?: string;
  order?: ApiRelation | null;
  order_id?: string | null;
  order_no?: string | null;
  material_request?: ApiMaterialRequestRelation | null;
  material_request_id?: string | null;
  material_request_no?: string | null;
  picker?: ApiRelation | null;
  picker_id?: string | null;
  picker_name?: string | null;
  issued_by_id?: string | null;
  issued_by_name?: string | null;
  received_by_id?: string | null;
  received_by_name?: string | null;
  created_by?: ApiRelation | null;
  created_by_name?: string | null;
  note?: string;
  total_amount?: string | number | null;
  lines?: ApiIssueLine[];
  [key: string]: any;
}

export interface CreateIssueLinePayload {
  item: string;
  uom?: string;
  source_request_line?: string;
  order_item?: string;
  issued_qty: number;
  unit_cost?: number;
  note?: string;
}

export interface CreateIssuePayload {
  company: string;
  warehouse: string;
  issued_at: string;
  order?: string;
  status?: string;
  content?: string;
  picker?: string;
  note?: string;
  issued_by_id?: string;
  issued_by_name?: string;
  received_by_id?: string;
  received_by_name?: string;
  lines: CreateIssueLinePayload[];
}

export interface UpdateIssuePayload extends CreateIssuePayload {
  id: string;
}

interface PickIssueLinePayload {
  line_id: string;
  picked_qty: number;
}

const notifyIssueChanged = (issueId?: string) => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('inventory:issue-changed', {
      detail: { issueId },
    }));
  }
};

const unwrapListResponse = (data: any): InventoryIssueListResponse => {
  if (Array.isArray(data)) {
    return {
      results: data,
      count: data.length,
      page: 1,
      page_size: data.length || PAGE_SIZE,
      total_pages: 1,
    };
  }

  return {
    results: data?.results || [],
    count: data?.count || 0,
    page: data?.page || 1,
    page_size: data?.page_size || PAGE_SIZE,
    total_pages: data?.total_pages || 1,
  };
};

const toNumber = (value: unknown) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeStatus = (issue: ApiIssue) => {
  const relationStatusCode = typeof issue.status === 'object' && issue.status ? issue.status.code : '';
  const directStatusCode = typeof issue.status === 'string' ? issue.status : '';
  return String(issue.status_code || relationStatusCode || directStatusCode || '').trim().toLowerCase();
};

const mapAmount = (issue: ApiIssue): number => {
  if (issue.total_amount !== undefined && issue.total_amount !== null) {
    return toNumber(issue.total_amount);
  }

  const lines = Array.isArray(issue.lines) ? issue.lines : [];
  return lines.reduce((sum, line) => sum + toNumber(line.line_amount), 0);
};

const resolveLineOrderCode = (line: ApiIssueLine): string => {
  const candidates: Array<unknown> = [
    line.order_code,
    line.order_no,
    line.source_order_code,
    line.source_order_no,
    line.order?.code,
    line.order?.name,
    (line as any).order_item_code,
    (line as any).order_item_order_code,
    (line as any).order_item?.order_code,
    (line as any).order_item?.order_no,
    (line as any).order_item?.order?.code,
    (line as any).source_request_line_order_code,
    (line as any).source_request_line_order_no,
    (line as any).source_request_line?.order_code,
    (line as any).source_request_line?.order_no,
    (line as any).source_request_line?.order?.code,
    (line as any).source_request_line?.request?.order?.code,
    (line as any).source_request_line?.material_request?.order?.code,
  ];

  for (const candidate of candidates) {
    const value = String(candidate || '').trim();
    if (value) {
      return value;
    }
  }

  return '';
};

const mapLine = (line: ApiIssueLine, index: number): InventoryIssueLineRow => ({
  id: String(line.id || `line-${index}`),
  item_id: line.item_id ? String(line.item_id) : undefined,
  item_code: line.item_code || '',
  item_name: line.item_name || '',
  order_no: resolveLineOrderCode(line),
  order_code: resolveLineOrderCode(line),
  raw: line,
  uom_id: line.uom_id ? String(line.uom_id) : undefined,
  uom_name: line.uom_name || '',
  issued_qty: toNumber(line.issued_qty),
  unit_cost: toNumber(line.unit_cost),
  line_amount: toNumber(line.line_amount),
  in_progress_qty: toNumber(line.in_progress_qty),
  note: line.note || '',
});

const toRow = (issue: ApiIssue): InventoryIssueRow => {
  const statusCode = normalizeStatus(issue);
  const companyId = issue.company_id || issue.company?.id || '';
  const companyName = issue.company_name || issue.company?.name || '';
  const warehouseId = issue.warehouse_id || issue.warehouse?.id || '';
  const warehouseName = issue.warehouse_name || issue.warehouse?.name || '';
  const orderId = issue.order_id || issue.order?.id || '';
  const orderNo = issue.order_no || issue.order?.code || '';
  const customerName = issue.customer_name || issue.order?.customer_name || '';
  const orderCustomerName = issue.order?.customer_name || '';
  const orderStatusColor = issue.order?.status?.color || '';
  const pickerName = issue.picker_name || issue.picker?.name || '';
  const createdByName = issue.created_by_name || issue.created_by?.name || issue.created_by?.username || '';
  const createdById = issue.created_by_id || issue.created_by?.id || '';
  const updatedByName = issue.updated_by_name || issue.updated_by?.name || issue.updated_by?.username || '';
  const updatedById = issue.updated_by_id || issue.updated_by?.id || '';
  const statusDisplay = issue.status_display
    || (typeof issue.status === 'object' && issue.status ? issue.status.name : '')
    || (typeof issue.status === 'string' ? issue.status : '');

  return {
    id: String(issue.id),
    code: issue.issue_no || '',
    name: issue.content || '',
    issued_at: issue.issued_at || undefined,
    picker_name: pickerName,
    issued_by_id: issue.issued_by_id || undefined,
    issued_by_name: issue.issued_by_name || undefined,
    received_by_id: issue.received_by_id || undefined,
    received_by_name: issue.received_by_name || undefined,
    created_by_name: createdByName,
    created_by: createdById,
    updated_by_name: updatedByName,
    updated_by: updatedById,
    order_id: orderId,
    order_no: orderNo,
    customer_name: customerName,
    order_customer_name: orderCustomerName,
    order_status_color: orderStatusColor,
    material_request_id: issue.material_request_id || issue.material_request?.id || undefined,
    material_request_no: issue.material_request_no || issue.material_request?.request_no || '',
    created_at: issue.created_at || undefined,
    updated_at: issue.updated_at || undefined,
    posted_at: issue.posted_at !== undefined ? issue.posted_at : undefined,
    warehouse_id: warehouseId,
    store_name: warehouseName,
    status: issue.status || statusCode,
    status_code: statusCode,
    status_display: statusDisplay,
    amount: mapAmount(issue),
    company_id: companyId,
    company_name: companyName,
    note: issue.note || '',
    lines: (Array.isArray(issue.lines) ? issue.lines : []).map(mapLine),
  };
};

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const resolveIssuesCompanyId = (companyId?: string) => {
  const resolved = (companyId || getCurrentCompanyId() || '').trim();
  if (!resolved || resolved === 'global' || resolved === 'all' || !UUID_PATTERN.test(resolved)) {
    return undefined;
  }
  return resolved;
};

const fetchIssues = async ({ search = '', status = 'all', companyId = '', itemId = '', page = 1, pageSize = PAGE_SIZE, listType }: InventoryIssueQueryParams & { listType?: 'need_export' | 'need_posting' } = {}): Promise<InventoryIssueListResponse> => {
  const params: Record<string, string | number> = {
    page,
    page_size: pageSize || PAGE_SIZE,
  };

  if (search.trim()) {
    params.search = search.trim();
  }

  const normalizedCompanyId = resolveIssuesCompanyId(companyId);
  if (normalizedCompanyId) {
    params.company = normalizedCompanyId;
  }

  if (itemId) {
    params.item = itemId;
  }

  if (status && status !== 'all') {
    params.status_code = status;
  }

  if (listType) {
    params.list_type = listType;
  }

  const response = await axiosInstance.get('/inventory/issues/', { params });
  const parsed = unwrapListResponse(response.data);

  return {
    ...parsed,
    results: (parsed.results || []).map((issue: any) => toRow(issue as ApiIssue)),
  };
};

export const getNeedPostingIssues = async (params: InventoryIssueQueryParams = {}): Promise<InventoryIssueListResponse> => {
  return fetchIssues({
    ...params,
  });
};

export const getIssues = async (params: InventoryIssueQueryParams = {}): Promise<InventoryIssueListResponse> => {
  return fetchIssues({
    ...params,
  });
};

export const getNeedExportIssues = async (params: InventoryIssueQueryParams = {}): Promise<InventoryIssueListResponse> => {
  return fetchIssues({
    ...params,
    listType: 'need_export',
  });
};

export const getIssueById = async (id: string): Promise<InventoryIssueRow> => {
  const response = await axiosInstance.get(`/inventory/issues/${id}/`);
  return toRow(response.data as ApiIssue);
};

export const createIssue = async (payload: CreateIssuePayload): Promise<ApiIssue> => {
  const response = await axiosInstance.post('/inventory/issues/', payload);
  notifyIssueChanged(response.data?.id);
  return response.data as ApiIssue;
};

export const updateIssue = async (payload: UpdateIssuePayload): Promise<ApiIssue> => {
  const { id, ...body } = payload;
  const response = await axiosInstance.put(`/inventory/issues/${id}/`, body);
  notifyIssueChanged(id);
  return response.data as ApiIssue;
};

export const pickIssue = async (issueId: string, payload: { lines: PickIssueLinePayload[]; issued_at?: string; note?: string; picker_id?: string }): Promise<ApiIssue> => {
  const response = await axiosInstance.post(`/inventory/issues/${issueId}/pick/`, payload);
  notifyIssueChanged(issueId);
  return response.data as ApiIssue;
};

export const postIssue = async (issueId: string): Promise<ApiIssue> => {
  const response = await axiosInstance.post(`/inventory/issues/${issueId}/post/`, {});
  notifyIssueChanged(issueId);
  return response.data as ApiIssue;
};

export const cancelIssue = async (issueId: string): Promise<ApiIssue> => {
  const response = await axiosInstance.post(`/inventory/issues/${issueId}/cancel/`, {});
  notifyIssueChanged(issueId);
  return response.data as ApiIssue;
};
